import React from 'react';
import { Row, Col, Input, DatePicker, Button, Space } from 'antd';
import { SearchOutlined, ClearOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import LookupField from '../../../components/LookupField';
import type { QueryRequest } from '../../../services/pwm211Api';

interface FilterBarProps {
  filters: QueryRequest;
  loading: boolean;
  onSetFilter: (key: keyof QueryRequest, value: string) => void;
  onSearch: () => void;
  onReset: () => void;
}

const FilterBar: React.FC<FilterBarProps> = React.memo(({
  filters, loading, onSetFilter, onSearch, onReset,
}) => {
  return (
    <div className="pwm211-filter-bar">
      <Row gutter={[8, 8]} align="bottom">
        {/* 庫位代號範圍 */}
        <Col>
          <div className="filter-label">庫位代號（起）</div>
          <LookupField
            lookupType="warehouse"
            value={filters.start_loc ?? ''}
            onChange={(v) => onSetFilter('start_loc', v)}
            onCodeConfirm={(v) => {
              if (v) onSetFilter('last_loc', v + '\xff');
            }}
            maxLength={5}
            style={{ width: 130 }}
          />
        </Col>
        <Col>
          <div className="filter-label">[至]</div>
          <LookupField
            lookupType="warehouse"
            value={filters.last_loc ?? ''}
            onChange={(v) => onSetFilter('last_loc', v)}
            onCodeConfirm={() => {}}
            maxLength={5}
            style={{ width: 130 }}
          />
        </Col>
        {/* 產品代號範圍 */}
        <Col>
          <div className="filter-label">產品代號（起）</div>
          <LookupField
            lookupType="product"
            value={filters.start_product ?? ''}
            onChange={(v) => onSetFilter('start_product', v)}
            onCodeConfirm={(v) => {
              if (v) {
                const padded = (v + 'zzzzzzzzzzzzzzzzzz').substring(0, 12);
                onSetFilter('last_product', padded);
              }
            }}
            maxLength={12}
            style={{ width: 160 }}
          />
        </Col>
        <Col>
          <div className="filter-label">[至]</div>
          <LookupField
            lookupType="product"
            value={filters.last_product ?? ''}
            onChange={(v) => onSetFilter('last_product', v)}
            onCodeConfirm={() => {}}
            maxLength={12}
            style={{ width: 160 }}
          />
        </Col>
        {/* 批號範圍 */}
        <Col>
          <div className="filter-label">批號（起）</div>
          <Input
            value={filters.start_batch ?? ''}
            onChange={e => onSetFilter('start_batch', e.target.value)}
            onBlur={() => {
              if (filters.start_batch) {
                const padded = (filters.start_batch + 'zzzzzzzzzzzzzzzzzz').substring(0, 12);
                onSetFilter('last_batch', padded);
              }
            }}
            placeholder="批號起"
            maxLength={12}
            style={{ width: 120 }}
          />
        </Col>
        <Col>
          <div className="filter-label">[至]</div>
          <Input
            value={filters.last_batch ?? ''}
            onChange={e => onSetFilter('last_batch', e.target.value)}
            placeholder="批號迄"
            maxLength={12}
            style={{ width: 120 }}
          />
        </Col>
        {/* 入庫日期範圍 */}
        <Col>
          <div className="filter-label">入庫日期（起）</div>
          <DatePicker
            value={filters.start_date ? dayjs(filters.start_date) : null}
            onChange={d => onSetFilter('start_date', d ? d.format('YYYY-MM-DD') : '')}
            format="YYYY/MM/DD"
            style={{ width: 130 }}
          />
        </Col>
        <Col>
          <div className="filter-label">[至]</div>
          <DatePicker
            value={filters.last_date ? dayjs(filters.last_date) : null}
            onChange={d => onSetFilter('last_date', d ? d.format('YYYY-MM-DD') : '')}
            format="YYYY/MM/DD"
            style={{ width: 130 }}
          />
        </Col>
        {/* Buttons */}
        <Col>
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              loading={loading}
              onClick={onSearch}
            >
              庫位確認
            </Button>
            <Button icon={<ClearOutlined />} onClick={onReset}>
              清除
            </Button>
          </Space>
        </Col>
      </Row>
    </div>
  );
});

FilterBar.displayName = 'FilterBar';

export default FilterBar;
